import type { SupabaseClient } from '@supabase/supabase-js';

const ASSURANCE_LEVEL_2 = 'aal2';

/**
 * @name checkRequiresMultiFactorAuthentication
 * @description Checks if the current session requires multi-factor authentication.
 * We do it by checking that the next assurance level is AAL2 and that the current assurance level is not AAL2.
 * @param client
 * @param accessToken Verify this token instead of the cookie session. Callers
 * that authenticate with a bearer token (the Office add-in task pane) have no
 * local session; passing the token makes `getAuthenticatorAssuranceLevel` read
 * `aal` from the JWT and the verified factors from `getUser(jwt)` instead.
 */
export async function checkRequiresMultiFactorAuthentication(
  client: SupabaseClient,
  accessToken?: string,
) {
  // Suppress the getSession warning. Remove when the issue is fixed.
  // https://github.com/supabase/auth-js/issues/873
  // @ts-expect-error: suppressGetSessionWarning is not part of the public API
  client.auth.suppressGetSessionWarning = true;

  const assuranceLevel =
    await client.auth.mfa.getAuthenticatorAssuranceLevel(accessToken);

  // @ts-expect-error: suppressGetSessionWarning is not part of the public API
  client.auth.suppressGetSessionWarning = false;

  if (assuranceLevel.error) {
    throw new Error(assuranceLevel.error.message);
  }

  const { nextLevel, currentLevel } = assuranceLevel.data;

  return nextLevel === ASSURANCE_LEVEL_2 && nextLevel !== currentLevel;
}
